import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../utils/api';
import './Settings.css';


export function Settings() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneVerified: false
  });
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);

  // Account Deletion States
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Original state tracking for changes
  const [originalProfile, setOriginalProfile] = useState<any>(null);
  const [originalPrefs, setOriginalPrefs] = useState<any>(null);

  // Validation States
  const [phoneError, setPhoneError] = useState(false);

  // Phone Verification States
  const [verificationMode, setVerificationMode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);

  // Notification Preferences States
  const [prefEmail, setPrefEmail] = useState(true);
  const [prefSms, setPrefSms] = useState(false);

  // Test-notification States
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<Record<string, string> | null>(null);

  // SMS Compliance Modals
  const [showSmsOptInConfirm, setShowSmsOptInConfirm] = useState(false);
  const [showSmsOptOutConfirm, setShowSmsOptOutConfirm] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const data = await api.get('/api/v1/users/me');
      const loadedProfile = {
        firstName: data.profile.firstName || '',
        lastName: data.profile.lastName || '',
        email: data.profile.email || '',
        phone: data.profile.phone || '',
        phoneVerified: data.profile.phoneVerified || false
      };
      setProfile(loadedProfile);
      setOriginalProfile(loadedProfile);
      setRoles(data.roles || []);

      // Load preferences
      try {
        const prefData = await api.get('/api/v1/users/me/notification-preferences');
        const loadedPrefs = {
          emailEnabled: prefData.emailEnabled !== false,
          smsEnabled: prefData.smsEnabled !== false
        };
        setPrefEmail(loadedPrefs.emailEnabled);
        setPrefSms(loadedPrefs.smsEnabled);
        setOriginalPrefs(loadedPrefs);
      } catch (prefErr) {
        console.error('Failed to load notification preferences', prefErr);
      }
    } catch (err) {
      console.error('Failed to load profile', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value: string) => {
    if (!value) return value;
    // Strip all non-numeric characters
    const phoneNumber = value.replace(/[^\d]/g, '');
    
    // Auto-formatting (XXX) XXX-XXXX
    const phoneNumberLength = phoneNumber.length;
    
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setProfile(prev => ({ ...prev, phone: formatted, phoneVerified: false })); // Changing phone un-verifies it
    setVerificationMode(false);
    setVerifyMessage(null);
    
    // Validate: valid if empty or exactly 14 characters "(123) 456-7890"
    if (formatted && formatted.length < 14) {
      setPhoneError(true);
    } else {
      setPhoneError(false);
    }
  };



  const hasProfileChanges = originalProfile && (
    profile.firstName !== originalProfile.firstName ||
    profile.lastName !== originalProfile.lastName ||
    profile.phone !== originalProfile.phone
  );

  const hasPrefChanges = originalPrefs && (
    prefEmail !== originalPrefs.emailEnabled ||
    prefSms !== originalPrefs.smsEnabled
  );

  const hasChanges = hasProfileChanges || hasPrefChanges;

  const handleSendTest = async () => {
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await api.post('/api/v1/users/me/test-notification');
      setTestResult(res?.results ?? {});
    } catch (err) {
      setTestResult({ error: 'Failed to send test notification. Please try again.' });
    } finally {
      setSendingTest(false);
    }
  };

  const handleSaveAll = async () => {
    if (phoneError) return;
    
    setSaving(true);
    setMessage(null);
    try {
      if (hasProfileChanges) {
        await api.put('/api/v1/users/me', {
          firstName: profile.firstName,
          lastName: profile.lastName,
          phone: profile.phone
        });
        setOriginalProfile({
          ...originalProfile,
          firstName: profile.firstName,
          lastName: profile.lastName,
          phone: profile.phone,
          phoneVerified: profile.phoneVerified
        });
      }
      
      if (hasPrefChanges) {
        await api.put('/api/v1/users/me/notification-preferences', {
          emailEnabled: prefEmail,
          smsEnabled: prefSms
        });
        setOriginalPrefs({
          ...originalPrefs,
          emailEnabled: prefEmail,
          smsEnabled: prefSms
        });
      }
      
      setMessage({ text: 'Changes saved successfully!', type: 'success' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ text: err.message || 'Failed to save changes', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendVerificationCode = async () => {
    if (phoneError || !profile.phone) return;
    setSendingCode(true);
    setVerifyMessage(null);
    try {
      // Automatically save the phone number to the profile first
      await api.put('/api/v1/users/me', {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone
      });
      
      // Update original profile so the Save button doesn't stay active unnecessarily
      if (originalProfile) {
        setOriginalProfile({
          ...originalProfile,
          phone: profile.phone
        });
      }

      await api.post('/api/v1/users/me/phone/send-verification');
      setVerificationMode(true);
      setVerifyMessage({ text: 'Verification code sent!', type: 'success' });
    } catch (err: any) {
      setVerifyMessage({ text: err.message || 'Failed to send code', type: 'error' });
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) return;
    setVerifying(true);
    setVerifyMessage(null);
    try {
      await api.post('/api/v1/users/me/phone/verify', { code: verificationCode });
      setProfile(prev => ({ ...prev, phoneVerified: true }));
      setVerificationMode(false);
      setVerifyMessage({ text: 'Phone verified successfully!', type: 'success' });
      setVerificationCode('');
    } catch (err: any) {
      setVerifyMessage({ text: err.message || 'Invalid verification code', type: 'error' });
    } finally {
      setVerifying(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      // 1. Delete DB profile, roles, and notifications
      await api.delete('/api/v1/users/me');

      // 2. Call Cognito to delete user account
      const { deleteUser } = await import('aws-amplify/auth');
      try {
        await deleteUser();
      } catch (cognitoErr) {
        console.error('Failed to delete Cognito user directly, logging out...', cognitoErr);
      }

      // 3. Logout and navigate to login
      await logout();
      navigate('/login');
    } catch (err: any) {
      setMessage({ text: err.message || 'Failed to delete account', type: 'error' });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="page settings-page"><div className="page__header"><h1 className="page__title">Account Settings</h1></div><p>Loading...</p></div>;
  }

  const isFormValid = !phoneError;

  return (
    <div className="page settings-page">
      <div className="page__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page__title">Account Settings</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {message && (
            <span style={{ fontSize: '0.9rem', color: message.type === 'success' ? 'var(--color-brand-primary)' : 'var(--color-status-critical)' }}>
              {message.text}
            </span>
          )}
          <button 
            className="btn-save-changes" 
            onClick={handleSaveAll} 
            disabled={!hasChanges || !isFormValid || saving}
            style={{ 
              opacity: (!hasChanges || !isFormValid || saving) ? 0.5 : 1, 
              cursor: (!hasChanges || !isFormValid || saving) ? 'not-allowed' : 'pointer' 
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Profile Form */}
      <div className="settings-section">
        <h2 className="settings-section__title">Profile</h2>
        <div className="settings-form card">
          <div className="form-row" style={{ marginBottom: '1.5rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label" htmlFor="settings-first-name">First Name</label>
              <input
                className="form-input"
                type="text"
                id="settings-first-name"
                placeholder="John"
                value={profile.firstName}
                onChange={e => setProfile({...profile, firstName: e.target.value})}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label" htmlFor="settings-last-name">Last Name</label>
              <input
                className="form-input"
                type="text"
                id="settings-last-name"
                placeholder="Smith"
                value={profile.lastName}
                onChange={e => setProfile({...profile, lastName: e.target.value})}
              />
            </div>
          </div>
          <div className="form-row" style={{ marginBottom: '1.5rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label" htmlFor="settings-email">Email (Read-Only)</label>
              <input
                className="form-input"
                type="email"
                id="settings-email"
                placeholder="john.smith@example.com"
                value={profile.email}
                disabled
                style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed', color: 'var(--text-secondary)' }}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label" htmlFor="settings-phone" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Phone Number</span>
                {profile.phone && !phoneError && (
                  <span className={`badge ${profile.phoneVerified ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>
                    {profile.phoneVerified ? 'Verified' : 'Unverified'}
                  </span>
                )}
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  className={`form-input ${phoneError ? 'form-input--invalid' : ''}`}
                  type="tel"
                  id="settings-phone"
                  placeholder="(403) 555-1234"
                  value={profile.phone}
                  onChange={handlePhoneChange}
                  style={{ flex: 1 }}
                />
                {!profile.phoneVerified && profile.phone && !phoneError && !verificationMode && (
                  <button 
                    className="btn-verify" 
                    onClick={handleSendVerificationCode}
                    disabled={sendingCode || saving}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {sendingCode ? 'Sending...' : 'Verify'}
                  </button>
                )}
              </div>
              {phoneError && <span className="validation-error">Must be a valid 10-digit number</span>}
              
              {verificationMode && !profile.phoneVerified && (
                <div className="verification-box" style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border-primary)' }}>
                  <label className="form-label" htmlFor="verify-code">Enter Code</label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                    <input
                      className="form-input"
                      type="text"
                      id="verify-code"
                      placeholder="123456"
                      maxLength={6}
                      value={verificationCode}
                      onChange={e => setVerificationCode(e.target.value.replace(/[^\d]/g, ''))}
                      style={{ flex: 1, letterSpacing: '2px' }}
                    />
                    <button 
                      className="view-toggle-btn view-toggle-btn--active" 
                      onClick={handleVerifyCode}
                      disabled={verifying || verificationCode.length !== 6}
                    >
                      {verifying ? 'Verifying...' : 'Submit'}
                    </button>
                  </div>
                </div>
              )}
              {verifyMessage && (
                <span style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.85rem', color: verifyMessage.type === 'success' ? 'var(--color-brand-primary)' : 'var(--color-status-critical)' }}>
                  {verifyMessage.text}
                </span>
              )}
            </div>
          </div>
          

        </div>
      </div>

      {/* Notification Settings */}
      <div className="settings-section">
        <h2 className="settings-section__title">Notification Settings</h2>
        <div className="settings-form card">
          <div className="form-group" style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontSize: '1rem', color: 'var(--text-primary)' }}>
              <input
                type="checkbox"
                checked={prefEmail}
                onChange={e => setPrefEmail(e.target.checked)}
                style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
              />
              Email Notifications
            </label>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: (!profile.phoneVerified && !prefSms) ? 'not-allowed' : 'pointer', fontSize: '1rem', color: (!profile.phoneVerified && !prefSms) ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
              <input
                type="checkbox"
                checked={prefSms}
                disabled={!profile.phoneVerified && !prefSms}
                onChange={e => {
                  if (e.target.checked) {
                    setShowSmsOptInConfirm(true);
                  } else {
                    setShowSmsOptOutConfirm(true);
                  }
                }}
                style={{ width: '1.2rem', height: '1.2rem', cursor: (!profile.phoneVerified && !prefSms) ? 'not-allowed' : 'pointer' }}
              />
              SMS Notifications
              {!profile.phoneVerified && !prefSms && (
                <span style={{ fontSize: '0.8rem', color: 'var(--color-status-warning)' }}>
                  (Requires verified phone number)
                </span>
              )}
            </label>
          </div>

          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.25rem' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem' }}>
              Send a test notification to your enabled channels to confirm delivery works.
            </p>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleSendTest}
              disabled={sendingTest}
              style={{ cursor: sendingTest ? 'wait' : 'pointer' }}
            >
              {sendingTest ? 'Sending…' : 'Send test notification'}
            </button>
            {testResult && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0 0', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {Object.entries(testResult).map(([channel, status]) => {
                  const sent = status === 'sent';
                  const failed = status === 'error' || channel === 'error';
                  const color = sent
                    ? 'var(--color-status-good)'
                    : failed
                      ? 'var(--color-status-critical)'
                      : 'var(--text-tertiary)';
                  const label = channel === 'error' ? 'Error' : channel === 'sms' ? 'SMS' : channel === 'email' ? 'Email' : channel;
                  return (
                    <li key={channel} style={{ fontSize: '0.85rem', color }}>
                      <strong style={{ textTransform: 'capitalize' }}>{label}:</strong> {channel === 'error' ? status : status}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

        </div>
      </div>

      {/* Roles & Assignments */}
      <div className="settings-section">
        <h2 className="settings-section__title">Roles &amp; Assignments</h2>
        <div className="card" style={{ padding: '0' }}>
          {roles.length > 0 ? (
            <ul className="user-list" style={{ margin: 0 }}>
              {roles.map(role => (
                <li key={role.SK} className="user-list-item" style={{ padding: '1rem 2rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="user-info">
                    <span className="user-email" style={{ fontSize: '1.1rem', color: 'var(--text-primary)' }}>{role.role}</span>
                    <span className="user-role" style={{ color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                      {role.siteId ? `Site Access (ID: ${role.siteId})` : `Global Organization Access (ID: ${role.tenantId})`}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="placeholder-text" style={{ padding: '2rem', color: 'var(--text-secondary)', margin: 0 }}>
              No roles found.
            </p>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="settings-section">
        <h2 className="settings-section__title" style={{ color: 'var(--color-status-critical)' }}>Danger Zone</h2>
        <div className="card" style={{ padding: '2rem' }}>
          <button 
            type="button" 
            className="btn-delete-account" 
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            style={{ marginTop: 0 }}
          >
            {deleting ? 'Deleting Account...' : 'Delete Account'}
          </button>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h2 style={{ color: 'var(--color-status-critical)' }}>Delete Account</h2>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              <p>
                Are you sure you want to permanently delete your account? This action is permanent and cannot be undone. All your settings, roles, and notifications will be removed.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-danger"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS Opt-In Confirmation Modal */}
      {showSmsOptInConfirm && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h2>Enable SMS Alerts</h2>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              <p>Message frequency varies based on your alert settings.</p>
              <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                By continuing, you agree to our <a href="#tos" style={{ color: 'var(--color-brand-primary)' }}>Terms of Service</a> and <a href="#privacy" style={{ color: 'var(--color-brand-primary)' }}>Privacy Policy</a>.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setShowSmsOptInConfirm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-primary"
                onClick={async () => {
                  setShowSmsOptInConfirm(false);
                  setPrefSms(true);
                  
                  setSaving(true);
                  setMessage(null);
                  try {
                    await api.put('/api/v1/users/me/notification-preferences', {
                      emailEnabled: prefEmail,
                      smsEnabled: true
                    });
                    setOriginalPrefs({ ...originalPrefs, smsEnabled: true });
                    setMessage({ text: 'SMS Alerts enabled successfully! Welcome text sent.', type: 'success' });
                    setTimeout(() => setMessage(null), 4000);
                  } catch (err: any) {
                    setPrefSms(false); // revert
                    setMessage({ text: err.message || 'Failed to enable SMS Alerts', type: 'error' });
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS Opt-Out Confirmation Modal */}
      {showSmsOptOutConfirm && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h2 style={{ color: 'var(--color-status-critical)' }}>Disable SMS Alerts</h2>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              <p>Are you sure you want to disable SMS alerts? You will no longer receive text messages for critical thresholds.</p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setShowSmsOptOutConfirm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-danger"
                onClick={async () => {
                  setShowSmsOptOutConfirm(false);
                  setPrefSms(false);
                  
                  setSaving(true);
                  setMessage(null);
                  try {
                    await api.put('/api/v1/users/me/notification-preferences', {
                      emailEnabled: prefEmail,
                      smsEnabled: false
                    });
                    setOriginalPrefs({ ...originalPrefs, smsEnabled: false });
                    setMessage({ text: 'SMS Alerts disabled successfully.', type: 'success' });
                    setTimeout(() => setMessage(null), 3000);
                  } catch (err: any) {
                    setPrefSms(true); // revert
                    setMessage({ text: err.message || 'Failed to disable SMS Alerts', type: 'error' });
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
